import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowRight, ShieldCheck, Mail, Phone, Lock, Sparkles, User, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

// Re-enabling this requires a reviewed backend SMS integration, not an environment toggle.
import { PHONE_AUTH_ENABLED } from "@/lib/authConfig";

export default function Register() {
  const { login, loginWithGoogle, refreshUser, isAuthProcessing } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || location.state?.from || "/dashboard";
  const { register, handleSubmit, formState: { isSubmitting } } = useForm();

  const [screen, setScreen] = useState("signup"); // "signup" | "verify" | "link-phone"
  const [signupMode, setSignupMode] = useState("email"); // "email" | "phone" | "google"
  const [roleTab, setRoleTab] = useState(location.state?.role === "agent" ? "agent" : "buyer"); // "buyer" | "agent"
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  // OTP Verification details
  const [verificationTarget, setVerificationTarget] = useState({ type: "email", value: "", name: "", password: "" });
  const [otpCode, setOtpCode] = useState("");

  // Google Login linking details
  const [linkPhoneVal, setLinkPhoneVal] = useState("");
  const [linkOtpCode, setLinkOtpCode] = useState("");
  const [linkOtpSent, setLinkOtpSent] = useState(false);
  const [tempUser, setTempUser] = useState(null);

  // Cooldown countdown timer effect
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Submit handler for Email and Phone signup
  const onSubmit = async (values) => {
    setBusy(true);
    try {
      const payload = {
        name: values.name,
        password: values.password,
        // "AGENT" only records an onboarding intent server-side; the account
        // starts as BUYER and agent privileges require admin approval.
        role: roleTab === "agent" ? "AGENT" : "BUYER",
        city: values.city || ""
      };
      if (values.phone) {
        payload.phone = values.phone;
      }

      if (signupMode === "email") {
        payload.email = values.email;
        setVerificationTarget({ type: "email", value: values.email, name: values.name, password: values.password });
      } else {
        payload.phone = values.phone;
        setVerificationTarget({ type: "phone", value: values.phone, name: values.name, password: values.password });
      }

      // Step 1: create (or safely resume) the pending account. Once this
      // succeeds, keep the user on the verification screen even if delivery is
      // temporarily unavailable; retrying registration can otherwise hit the
      // duplicate-account guard before they ever reach Resend.
      await api.post("/auth/register", payload);
      setScreen("verify");

      // Step 2: Trigger OTP send
      const otpPayload = signupMode === "email" ? { email: values.email } : { phone: values.phone };
      try {
        await api.post("/auth/otp/send", otpPayload);
        setCooldown(60);
        toast.success("Verification OTP code sent!");
      } catch (deliveryError) {
        toast.error(`${formatApiError(deliveryError)} Use Resend to try again.`);
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  // Resend OTP handler for signup flow
  const handleResendOtp = async () => {
    if (cooldown > 0) return;
    setBusy(true);
    try {
      const otpPayload = verificationTarget.type === "email" 
        ? { email: verificationTarget.value } 
        : { phone: verificationTarget.value };
      await api.post("/auth/otp/send", otpPayload);
      setCooldown(60);
      toast.success("OTP verification code resent successfully!");
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to resend OTP");
    } finally {
      setBusy(false);
    }
  };

  // OTP Verification for Signup
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast.error("Please enter the 6-digit OTP code");
      return;
    }
    setBusy(true);
    try {
      const payload = { otp: otpCode };
      if (verificationTarget.type === "email") {
        payload.email = verificationTarget.value;
      } else {
        payload.phone = verificationTarget.value;
      }
      
      // Call verify endpoint
      await api.post("/auth/verify-otp", payload);
      toast.success("Verification successful!");

      // Log the user in automatically
      const res = await login(verificationTarget.value, verificationTarget.password);

      if (res.ok) {
        navigate(from, { replace: true, state: { scrollY: location.state?.scrollY } });
      } else {
        toast.info("Please log in with your verified credentials.");
        navigate("/login", { state: { from, scrollY: location.state?.scrollY }, replace: true });
      }
    } catch (err) {
      toast.error(formatApiError(err) || "OTP verification failed");
    } finally {
      setBusy(false);
    }
  };

  // Google OAuth Initialization
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const targetId = signupMode === "email" ? "google-signup-div-email" : "google-signup-div-phone";
    const target = document.getElementById(targetId);
    if (!clientId || !target) return;

    const existing = document.querySelector('script[data-google-identity="true"]');
    const render = () => {
      if (!window.google || !target) return;
      target.innerHTML = "";
      window.google.accounts.id.initialize({ client_id: clientId, callback: handleGoogleCredential });
      window.google.accounts.id.renderButton(target, { theme: "outline", size: "large", width: Math.min(384, target.clientWidth || 384) });
    };

    if (existing) { render(); return; }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.dataset.googleIdentity = "true";
    script.onload = render;
    document.body.appendChild(script);
  }, [signupMode]);

  // Google credential handling
  const handleGoogleCredential = async (response) => {
    setBusy(true);
    try {
      const result = await loginWithGoogle(response.credential);
      if (!result.ok) {
        toast.error(result.error || "Google sign-up failed");
        return;
      }

      // Check if user has phone number linked
      if (PHONE_AUTH_ENABLED && !result.user.phone) {
        setTempUser(result.user);
        setScreen("link-phone");
      } else {
        toast.success("Successfully signed up with Google!");
        navigate(from, { replace: true, state: { scrollY: location.state?.scrollY } });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Google flow: Send Linking Phone OTP
  const handleSendLinkPhoneOtp = async (e) => {
    if (e) e.preventDefault();
    if (linkPhoneVal.length < 8) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/otp/send", { phone: linkPhoneVal });
      setLinkOtpSent(true);
      setCooldown(60);
      toast.success("OTP sent to phone!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Google flow: Verify Linking Phone OTP
  const handleVerifyLinkPhoneOtp = async (e) => {
    e.preventDefault();
    if (linkOtpCode.length !== 6) {
      toast.error("Please enter a 6-digit OTP code");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/verify-otp", {
        user_id: tempUser.id || tempUser.user_id,
        phone: linkPhoneVal,
        otp: linkOtpCode
      });
      toast.success("Phone verified and linked successfully!");
      await refreshUser();
      navigate(from, { replace: true, state: { scrollY: location.state?.scrollY } });
    } catch (err) {
      toast.error(formatApiError(err) || "OTP verification failed");
    } finally {
      setBusy(false);
    }
  };

  // Google flow: Skip Phone Linking
  const handleSkipPhoneLink = () => {
    toast.success("Welcome to ShreeHomes!");
    navigate(from, { replace: true, state: { scrollY: location.state?.scrollY } });
  };

  return (
    <div className="site-shell min-h-screen grid lg:grid-cols-2 bg-background w-full overflow-y-auto" data-testid="register-page">
      {/* Left side: Signup Panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 lg:p-16 order-2 lg:order-1 w-full my-auto">
        <div className="premium-card w-full max-w-lg bg-card border border-border/60 rounded-3xl p-8 sm:p-10">
          <Link to="/" className="mb-8 inline-flex lg:hidden" aria-label="ShreeHomes home">
            <BrandLogo size="sm" subtitle="The real place for real estate" />
          </Link>
          <p className="gold-eyebrow text-xs font-bold uppercase tracking-[0.25em] mb-3">Join ShreeHomes</p>

          {screen === "signup" && (
            <>
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-primary">Create your account</h1>

              {/* Role selector: buyers/owners sign up directly; agents are
                  verified and approved by the admin after signup. */}
              <div className="mt-6 grid grid-cols-2 gap-2 rounded-xl bg-muted p-1" data-testid="signup-role-toggle" role="tablist" aria-label="Sign up as">
                {[["buyer", "Buyer / Owner"], ["agent", "Agent"]].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={roleTab === value}
                    onClick={() => setRoleTab(value)}
                    className={`rounded-lg py-2 text-sm font-bold transition-all ${
                      roleTab === value
                        ? "bg-background text-primary shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`signup-role-${value}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {roleTab === "agent" && (
                <p className="mt-3 rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground" data-testid="signup-agent-note">
                  Create your account first — then complete agent verification (selfie, government ID) from your dashboard. An administrator approves every agent profile.
                </p>
              )}

              {/* Tab Selector */}
              <div className="mt-6 flex border-b border-border" data-testid="signup-mode-toggle">
                {(PHONE_AUTH_ENABLED ? ["email", "phone"] : ["email"]).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setSignupMode(mode)}
                    className={`flex-1 pb-3 text-sm font-semibold capitalize transition-all border-b-2 ${
                      signupMode === mode
                        ? "text-primary border-primary font-bold"
                        : "text-muted-foreground border-transparent hover:text-foreground"
                    }`}
                    data-testid={`signup-mode-${mode}`}
                  >
                    {mode === "email" ? "Email & Password" : "Phone & Password"}
                  </button>
                ))}
              </div>

              {signupMode === "email" && (
                <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5"><User className="w-4 h-4 text-muted-foreground"/>Full Name</Label>
                    <Input {...register("name", { required: true })} placeholder="Rahul Sharma" data-testid="reg-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5"><Mail className="w-4 h-4 text-muted-foreground"/>Email Address</Label>
                    <Input type="email" {...register("email", { required: true })} placeholder="you@example.com" data-testid="reg-email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-muted-foreground"/>Mobile Number{PHONE_AUTH_ENABLED ? "" : " (Optional)"}</Label>
                    <Input type="tel" {...register("phone", { required: PHONE_AUTH_ENABLED })} placeholder="+919999999999" data-testid="reg-phone-optional" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-muted-foreground"/>Password</Label>
                    <div className="relative">
                      <Input type={showPassword ? "text" : "password"} {...register("password", { required: true, minLength: 8, maxLength: 72 })} placeholder="At least 8 characters" data-testid="reg-password" className="pr-10" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>City (Optional)</Label>
                    <Input {...register("city")} placeholder="New Delhi" data-testid="reg-city" />
                  </div>
                  <Button type="submit" disabled={busy || isSubmitting || isAuthProcessing} className="w-full font-bold gap-2 mt-2" data-testid="register-submit">
                    {busy || isSubmitting || isAuthProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-white" />
                        <span>Creating account…</span>
                      </>
                    ) : (
                      <>
                        <span>Create account</span>
                        <ArrowRight className="w-4 h-4"/>
                      </>
                    )}
                  </Button>

                  <div className="relative flex py-4 items-center">
                    <div className="flex-grow border-t border-border"></div>
                    <span className="flex-shrink mx-4 text-muted-foreground text-xs font-semibold uppercase tracking-wider">Or continue with</span>
                    <div className="flex-grow border-t border-border"></div>
                  </div>

                  {process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
                    <div id="google-signup-div-email" className="w-full flex justify-center py-2" data-testid="google-login-button" />
                  ) : (
                    <div className="rounded-xl border border-dashed border-border bg-secondary/50 p-3 text-center text-xs text-muted-foreground" data-testid="google-login-disabled">
                      Google sign-in is disabled until REACT_APP_GOOGLE_CLIENT_ID is configured.
                    </div>
                  )}
                </form>
              )}

              {signupMode === "phone" && (
                <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5"><User className="w-4 h-4 text-muted-foreground"/>Full Name</Label>
                    <Input {...register("name", { required: true })} placeholder="Rahul Sharma" data-testid="reg-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-muted-foreground"/>Phone Number</Label>
                    <Input type="tel" {...register("phone", { required: true })} placeholder="+919999999999" data-testid="reg-phone" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-muted-foreground"/>Password</Label>
                    <div className="relative">
                      <Input type={showPassword ? "text" : "password"} {...register("password", { required: true, minLength: 8, maxLength: 72 })} placeholder="At least 8 characters" data-testid="reg-password" className="pr-10" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>City (Optional)</Label>
                    <Input {...register("city")} placeholder="New Delhi" data-testid="reg-city" />
                  </div>
                  <Button type="submit" disabled={busy || isSubmitting || isAuthProcessing} className="w-full font-bold gap-2 mt-2" data-testid="register-submit">
                    {busy || isSubmitting || isAuthProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-white" />
                        <span>Creating account…</span>
                      </>
                    ) : (
                      <>
                        <span>Create account</span>
                        <ArrowRight className="w-4 h-4"/>
                      </>
                    )}
                  </Button>

                  <div className="relative flex py-4 items-center">
                    <div className="flex-grow border-t border-border"></div>
                    <span className="flex-shrink mx-4 text-muted-foreground text-xs font-semibold uppercase tracking-wider">Or continue with</span>
                    <div className="flex-grow border-t border-border"></div>
                  </div>

                  {process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
                    <div id="google-signup-div-phone" className="w-full flex justify-center py-2" data-testid="google-login-button" />
                  ) : (
                    <div className="rounded-xl border border-dashed border-border bg-secondary/50 p-3 text-center text-xs text-muted-foreground" data-testid="google-login-disabled">
                      Google sign-in is disabled until REACT_APP_GOOGLE_CLIENT_ID is configured.
                    </div>
                  )}
                </form>
              )}
            </>
          )}

          {/* Screen: One-time Verification for Email / Phone signup */}
          {screen === "verify" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-10 h-10 text-primary shrink-0" />
                <div>
                  <h1 className="font-display text-2xl font-black tracking-tighter text-primary">Verify your {verificationTarget.type}</h1>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Enter the code sent to <strong className="text-foreground">{verificationTarget.value}</strong>
                  </p>
                </div>
              </div>
              <form onSubmit={handleVerifyOtp} className="space-y-4 pt-2">
                <div className="space-y-1.5">
                  <Label>6-Digit Verification Code</Label>
                  <Input
                    type="text"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="text-center font-bold tracking-[0.5em] text-xl h-12 bg-muted/30 focus-visible:bg-background"
                    data-testid="verify-otp-input"
                    required
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy || cooldown > 0}
                    onClick={handleResendOtp}
                    className="flex-1 font-semibold"
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Code"}
                  </Button>
                  <Button type="submit" disabled={busy} className="flex-1 font-bold">
                    {busy ? "Verifying…" : "Verify & Sign In"}
                  </Button>
                </div>
                <Button type="button" variant="link" onClick={() => setScreen("signup")} className="w-full text-xs text-muted-foreground">
                  ← Back to Registration
                </Button>
              </form>
            </div>
          )}

          {/* Screen: Google Sign-in Skippable Phone Link */}
          {screen === "link-phone" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Sparkles className="w-10 h-10 text-primary shrink-0 animate-pulse" />
                <div>
                  <h1 className="font-display text-2xl font-black tracking-tighter text-primary">Link Phone Number</h1>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Receive important listing and verification alerts on your phone.
                  </p>
                </div>
              </div>
              <form onSubmit={linkOtpSent ? handleVerifyLinkPhoneOtp : handleSendLinkPhoneOtp} className="space-y-4 pt-2">
                {!linkOtpSent ? (
                  <div className="space-y-1.5">
                    <Label>Mobile Number</Label>
                    <Input
                      type="tel"
                      value={linkPhoneVal}
                      onChange={(e) => setLinkPhoneVal(e.target.value)}
                      placeholder="+919999999999"
                      required
                    />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label>6-Digit Verification Code</Label>
                    <Input
                      type="text"
                      maxLength={6}
                      value={linkOtpCode}
                      onChange={(e) => setLinkOtpCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456"
                      className="text-center font-bold tracking-[0.5em] text-xl h-12 bg-muted/30 focus-visible:bg-background"
                      required
                    />
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={handleSkipPhoneLink} className="flex-1 font-semibold">
                    Skip for now
                  </Button>
                  <Button type="submit" disabled={busy} className="flex-1 font-bold">
                    {busy ? "Processing…" : (linkOtpSent ? "Verify & Link" : "Send OTP")}
                  </Button>
                </div>
                {linkOtpSent && (
                  <div className="flex flex-col items-center gap-1">
                    <Button type="button" variant="link" disabled={busy || cooldown > 0} onClick={handleSendLinkPhoneOtp} className="text-xs text-muted-foreground p-0 h-auto">
                      {cooldown > 0 ? `Resend Code in ${cooldown}s` : "Resend Verification Code"}
                    </Button>
                    <Button type="button" variant="link" onClick={() => setLinkOtpSent(false)} className="text-xs text-muted-foreground p-0 h-auto">
                      ← Change phone number
                    </Button>
                  </div>
                )}
              </form>
            </div>
          )}

          <p className="mt-8 text-sm text-muted-foreground text-center">
            Already have an account? <Link to="/login" state={{ from, scrollY: location.state?.scrollY }} className="text-primary font-bold hover:underline" data-testid="login-cta">Sign in</Link>
          </p>
        </div>
      </div>

      {/* Right side: Premium Branding */}
      <div className="premium-dark-panel hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-16 relative overflow-hidden order-1 lg:order-2 h-full min-h-screen">
        <Link to="/" className="mb-12 relative z-10 inline-flex" aria-label="ShreeHomes home">
          {/* Supplied logo anchors the desktop registration experience. */}
          <BrandLogo inverse size="md" subtitle="The real place for real estate" />
        </Link>
        <div className="my-auto relative z-10 max-w-md">
          <h2 className="font-display text-5xl font-black tracking-tighter leading-[1.05]">
            List properties. <em className="font-light">Reach serious buyers.</em>
          </h2>
          <p className="text-primary-foreground/75 mt-6 text-base leading-relaxed">Agents and offices: register to publish listings, manage appointments, and track leads in your dashboard.</p>
        </div>
        <div className="text-xs text-primary-foreground/45 mt-auto relative z-10">
          &copy; {new Date().getFullYear()} ShreeHomes. All rights reserved.
        </div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-primary-foreground/5 rounded-full animate-float-soft"/>
        <div className="absolute -bottom-16 -right-16 w-64 h-64 bg-primary-foreground/5 rounded-full animate-float-soft" style={{ animationDelay: "1.5s" }}/>
      </div>
    </div>
  );
}
